import React, { useState } from 'react';
import { useLocation, useParams } from '@tanstack/react-router';
import { Warning } from '@phosphor-icons/react';
import {
  Banner,
  DocsAnnotation,
  Drawer,
  DrawerTrigger,
  Flex,
  LoadingOrbit,
  Text,
} from '@chatsdet/ui';
import type { VideoTimelineEntry } from '@chatsdet/types';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import {
  useCancelExploratoryTest,
  useExploratoryTest,
  useRetestPrTest,
} from '../../features/exploratory-tests';
import { WEB_REVIEW_FLAVOR_LIST } from '../../features/exploratory-tests/flavors';
import type {
  ExploratoryTestListItem,
  SpecReviewReportJson,
  UxReviewReportJson,
} from '../../features/exploratory-tests/types';
import { useBrowserStream } from '../../hooks/useBrowserStream';
import { IssueDrawerV2 } from '../components/issues/IssueDrawerV2';
import { resolveVideoSrc } from '../utils/resolveVideoSrc';
import { ExploratoryDetailHeader } from './ExploratoryTestDetail/ExploratoryDetailHeader';
import { ExploratoryDetailMedia } from './ExploratoryTestDetail/ExploratoryDetailMedia';
import { ExploratoryDetailSidebar } from './ExploratoryTestDetail/ExploratoryDetailSidebar';
import { UxReviewSidebar } from './ExploratoryTestDetail/UxReviewSidebar';
import { SpecReviewSidebar } from './ExploratoryTestDetail/SpecReviewSidebar';
import { SpecReviewMainTabs } from './ExploratoryTestDetail/SpecReviewMainTabs';
import { shouldDefaultExploratorySidebarToReport } from './ExploratoryTestDetail/sidebar-default';
import { buildTimelineFromActions } from './ExploratoryTestDetail/timeline';
import type { SidebarTab } from './ExploratoryTestDetail/types';
export { mapRunToBar } from './ExploratoryRunHistoryControls';
export { deriveHeaderVerdict } from './ExploratoryTestVerdict';
export { UntestableSection } from './ExploratoryTestDetail/PlanPanel';
export {
  LiveTestPlanList,
  ObservationsSection,
  ReportPanel,
  TestPlanList,
} from './ExploratoryTestDetail/ReportPanel';
export { shouldDefaultExploratorySidebarToReport } from './ExploratoryTestDetail/sidebar-default';

function LockupShell({ children }: { children: React.ReactNode }) {
  return <div className="flex h-[calc(100vh-4rem)]">{children}</div>;
}

export function ExploratoryTestDetail() {
  // Route-agnostic: this component serves `/exploratory/$exploratoryTestId`
  // and every review flavor's Labs route (e.g. `/runs/ux-review/$id`,
  // `/runs/spec-review/$id`). `sectionMeta` resolves from the URL (not the
  // loaded test, which may not exist yet) so breadcrumbs are correct while
  // loading.
  const params = useParams({ strict: false }) as { exploratoryTestId: string };
  const exploratoryTestId = params.exploratoryTestId;
  const location = useLocation();
  const sectionMeta = WEB_REVIEW_FLAVOR_LIST.find((meta) =>
    location.pathname.startsWith(meta.listPath)
  );
  const testQuery = useExploratoryTest(exploratoryTestId);
  const cancelMutation = useCancelExploratoryTest();
  const retestMutation = useRetestPrTest();

  const [isIssueDrawerOpen, setIsIssueDrawerOpen] = useState(false);
  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<SidebarTab>('plan');
  const [selectedBrowserSessionId, setSelectedBrowserSessionId] = useState<string | null>(null);
  const didDefaultToReport = React.useRef(false);

  const test = testQuery.data?.data;
  const actions = testQuery.data?.actions ?? [];
  const candidateVerifications = testQuery.data?.candidateVerifications ?? [];
  const environmentInstance = testQuery.data?.environmentInstance ?? null;
  const reviewFindings = testQuery.data?.reviewFindings ?? [];
  const uxReport = (test?.reportJson ?? null) as unknown as UxReviewReportJson | null;
  const specReport = (test?.reportJson ?? null) as unknown as SpecReviewReportJson | null;

  const filedVerifications = candidateVerifications.filter((v) => v.filedIssueId);
  const pendingVerifications = candidateVerifications.filter((v) => !v.filedIssueId);
  const issuesCount = filedVerifications.length + pendingVerifications.length;

  const parentId = test?.exploratoryTestId ?? null;

  useBreadcrumbs([
    sectionMeta
      ? { key: sectionMeta.key, label: sectionMeta.breadcrumbLabel, href: sectionMeta.listPath }
      : { key: 'exploratory', label: 'Exploratory Tests', href: '/exploratory' },
    { key: 'detail', label: test?.title || (sectionMeta?.breadcrumbLabel ?? 'Exploratory Test') },
  ]);

  const isPlanning = test?.status === 'planning';
  const isRunning = test?.status === 'running' || test?.status === 'pending';
  const isInFlight = isPlanning || isRunning;

  React.useEffect(() => {
    if (didDefaultToReport.current) return;
    if (test && shouldDefaultExploratorySidebarToReport(test, isInFlight)) {
      didDefaultToReport.current = true;
      setActiveTab('report');
    }
  }, [test, isInFlight]);

  const browserSessions = testQuery.data?.browserSessions ?? [];
  const selectedBrowserSession =
    browserSessions.find((session) => session.id === selectedBrowserSessionId) ??
    browserSessions.find(
      (session) => session.status === 'active' || session.status === 'allocating'
    ) ??
    browserSessions[0];
  const streamId = selectedBrowserSession
    ? selectedBrowserSession.status === 'active' || selectedBrowserSession.status === 'allocating'
      ? selectedBrowserSession.browserSessionId
      : null
    : test?.targetKind !== 'sandbox-v2' && isInFlight
      ? exploratoryTestId
      : null;
  const { state: browserState } = useBrowserStream(streamId);

  const videoSrc = test?.videoStorageKey ? resolveVideoSrc(test.videoStorageKey) : null;

  const timeline = React.useMemo(() => {
    if (test?.videoTimelineJson) return test.videoTimelineJson;
    return buildTimelineFromActions(actions, test?.startedAt ?? null);
  }, [test?.videoTimelineJson, actions, test?.startedAt]);

  const openIssueForVerificationId = React.useCallback(
    (verificationId: string) => {
      const verification = candidateVerifications.find((v) => v.id === verificationId);
      if (!verification) return;
      if (verification.filedIssueId) {
        setSelectedIssueId(verification.filedIssueId);
        setIsIssueDrawerOpen(true);
      } else {
        setActiveTab('issues');
      }
    },
    [candidateVerifications]
  );

  const handleAnnotationClick = React.useCallback(
    (entry: VideoTimelineEntry) => {
      if (entry.annotationType === 'issue-filed' && entry.ref) {
        openIssueForVerificationId(entry.ref);
      }
    },
    [openIssueForVerificationId]
  );

  if (testQuery.isLoading) {
    return (
      <LockupShell>
        <div className="flex-1 flex items-center justify-center">
          <LoadingOrbit size="lg" />
        </div>
      </LockupShell>
    );
  }

  if (!test) {
    return (
      <LockupShell>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Text size="lg" weight="semibold" className="mb-2 block">
              Exploratory Test Not Found
            </Text>
            <Text className="text-muted-foreground">
              This test may have been deleted or you may not have access.
            </Text>
          </div>
        </div>
      </LockupShell>
    );
  }

  const title = test.title || 'Exploratory Test';

  return (
    <DocsAnnotation slug="reference/ad-hoc-tests" title="Exploratory Tests">
      <LockupShell>
        <div className="flex flex-col overflow-hidden flex-1 min-w-0">
          <ExploratoryDetailHeader
            key={test.id}
            test={test}
            title={title}
            parentId={parentId}
            currentRunId={exploratoryTestId}
            isInFlight={isInFlight}
            onCancel={() => cancelMutation.mutate(exploratoryTestId)}
            isCancelPending={cancelMutation.isPending}
            onRetest={(instructions) =>
              retestMutation.mutate({ sourceRunId: exploratoryTestId, instructions })
            }
            isRetestPending={retestMutation.isPending}
            retestError={
              retestMutation.error instanceof Error ? retestMutation.error.message : null
            }
          />

          {test.status === 'failed' && test.error && <FailureBanner message={test.error} />}
          <ArtifactAvailabilityBanner test={test} />

          {test.flavor === 'spec' ? (
            <SpecReviewMainTabs
              report={specReport}
              isPlanning={isPlanning}
              isRunning={isRunning}
              planningStartedAt={test.planningStartedAt ?? null}
              environmentInstance={environmentInstance}
              browserState={browserState}
              videoSrc={videoSrc}
              timeline={timeline}
              onAnnotationClick={handleAnnotationClick}
              browserSessions={browserSessions}
              runId={exploratoryTestId}
              selectedBrowserSessionId={selectedBrowserSession?.id ?? null}
              onBrowserSessionSelect={setSelectedBrowserSessionId}
            />
          ) : (
            <div className="flex-1 min-h-0 p-4 bg-muted/10">
              <ExploratoryDetailMedia
                isPlanning={isPlanning}
                isRunning={isRunning}
                planningStartedAt={test.planningStartedAt ?? null}
                environmentInstance={environmentInstance}
                browserState={browserState}
                videoSrc={videoSrc}
                timeline={timeline}
                onAnnotationClick={handleAnnotationClick}
                browserSessions={browserSessions}
                runId={exploratoryTestId}
                selectedBrowserSessionId={selectedBrowserSession?.id ?? null}
                onBrowserSessionSelect={setSelectedBrowserSessionId}
              />
            </div>
          )}
        </div>

        {test.flavor === 'ux' ? (
          <UxReviewSidebar
            report={uxReport}
            findings={reviewFindings}
            runId={exploratoryTestId}
            sessions={browserSessions}
            onSelectSession={setSelectedBrowserSessionId}
          />
        ) : test.flavor === 'spec' ? (
          <SpecReviewSidebar
            report={specReport}
            findings={reviewFindings}
            runId={exploratoryTestId}
            orchestration={test.specPreflightJson ?? null}
            ledger={test.specLedgerJson ?? null}
            sessions={browserSessions}
            onSelectSession={setSelectedBrowserSessionId}
          />
        ) : (
          <ExploratoryDetailSidebar
            activeTab={activeTab}
            issuesCount={issuesCount}
            test={test}
            filedVerifications={filedVerifications}
            pendingVerifications={pendingVerifications}
            onTabChange={setActiveTab}
            onOpenIssue={(issueId) => {
              setSelectedIssueId(issueId);
              setIsIssueDrawerOpen(true);
            }}
          />
        )}
      </LockupShell>

      {selectedIssueId && (
        <DrawerTrigger isOpen={isIssueDrawerOpen} onOpenChange={setIsIssueDrawerOpen}>
          <span style={{ display: 'none' }} />
          <Drawer hideCloseButton>
            <IssueDrawerV2
              issueId={selectedIssueId}
              pageIndex={0}
              totalItems={1}
              canNavigate={false}
              onNavigatePrev={() => {}}
              onNavigateNext={() => {}}
              onClose={() => setIsIssueDrawerOpen(false)}
            />
          </Drawer>
        </DrawerTrigger>
      )}
    </DocsAnnotation>
  );
}

function FailureBanner({ message }: { message: string }) {
  return (
    <div className="px-4 py-2 border-b border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
      <Flex align="start" gap="sm">
        <Warning
          size={16}
          weight="fill"
          className="text-red-600 dark:text-red-400 mt-0.5 shrink-0"
        />
        <Text size="sm" className="text-red-700 dark:text-red-300">
          {message}
        </Text>
      </Flex>
    </div>
  );
}

function ArtifactAvailabilityBanner({ test }: { test: ExploratoryTestListItem }) {
  // PR sandbox runs persist artifacts per browser session; the run-level slots
  // remain empty by design, so report missing media beside the selected session.
  if (test.status !== 'completed' || test.targetKind === 'sandbox-v2') return null;

  const unavailableArtifacts = [
    !test.videoStorageKey ? 'video recording' : null,
    !test.traceStorageKey ? 'Playwright trace' : null,
  ].filter((artifact): artifact is string => artifact !== null);

  if (unavailableArtifacts.length === 0) return null;

  const artifactList =
    unavailableArtifacts.length === 1
      ? unavailableArtifacts[0]
      : `${unavailableArtifacts[0]} and ${unavailableArtifacts[1]}`;

  return (
    <Banner variant="warning" className="mx-4 my-2">
      <Text size="sm">
        <strong>Some run artifacts are unavailable.</strong> The following artifacts are not
        available for this run: {artifactList}. The completed report and results are still
        available.
      </Text>
    </Banner>
  );
}
